import React, {useState, useEffect}  from "react";
import { useTapBindings } from "../lib/useTapBindings";
import {Marker, Polyline, Popup} from "react-leaflet";
import L from "leaflet";
import Moment from 'react-moment';
import 'leaflet-rotatedmarker';
import { decimalToDms } from '../lib/coordinates';
import LiveMarker from './LiveMarker';

// --- Definizioni Icone e SVG spostate FUORI dal componente ---
const svgIconSize = [80, 80]; // Dimensione comune per le icone di stato con cerchio
const svgIconAnchor = [40, 40]; // Ancoraggio centrale
const innerIconSize = 46; // Dimensione dell'icona interna
const innerIconOffset = (svgIconSize[0] - innerIconSize) / 2; // Offset per centrare l'icona interna
const circleRadius = 35;
const circleStrokeWidth = 2;


const createSvgIconHtml = (iconUrl, circleOptions = null) => {
    let circleHtml = '';
    let svgClass = ''; // Variabile per la classe SVG
    if (circleOptions) {
        svgClass = 'pulsating-icon'; // Applica la classe se c'è un cerchio
        circleHtml = `<circle cx="${svgIconAnchor[0]}" cy="${svgIconAnchor[1]}" r="${circleRadius}" stroke="${circleOptions.color}" stroke-width="${circleStrokeWidth}" fill="${circleOptions.fillColor}" fill-opacity="${circleOptions.fillOpacity}" />`;
    }
    return `
        <svg class="${svgClass}" width="${svgIconSize[0]}" height="${svgIconSize[1]}" viewBox="0 0 ${svgIconSize[0]} ${svgIconSize[1]}" xmlns="http://www.w3.org/2000/svg" style="overflow: visible;">
            ${circleHtml}
            <image href="${iconUrl}" x="${innerIconOffset}" y="${innerIconOffset}" width="${innerIconSize}" height="${innerIconSize}" />
        </svg>
    `;
};

const svgDirectionIconHtml = `
    <svg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg" style="overflow: visible;">
        <image href="./icons/IndicatoreDirezioneTracking.png" x="0" y="0" width="60" height="60" />
    </svg>
`;

// --- Nuove definizioni Icone con L.divIcon ---
const iconaTracking = L.divIcon({
    html: createSvgIconHtml('./icons/tracking.png'),
    iconSize: svgIconSize,
    iconAnchor: svgIconAnchor,
    className: 'leaflet-div-icon-transparent' // Classe per rimuovere sfondo/bordo default
});

const iconaNormal = L.divIcon({
    html: createSvgIconHtml('./icons/trackDROP.png', { color: 'green', fillColor: '#28a745', fillOpacity: 0.3 }),
    iconSize: svgIconSize,
    iconAnchor: svgIconAnchor,
    className: 'leaflet-div-icon-transparent'
});

const iconaAbnormal = L.divIcon({
    html: createSvgIconHtml('./icons/tracking-orange.png', { color: '#e67e00', fillColor: '#e67e00', fillOpacity: 0.3 }),
    iconSize: svgIconSize,
    iconAnchor: svgIconAnchor,
    className: 'leaflet-div-icon-transparent'
});

const iconaEmergency = L.divIcon({
    html: createSvgIconHtml('./icons/tracking-red.png', { color: 'red', fillColor: '#f03', fillOpacity: 0.3 }),
    iconSize: svgIconSize,
    iconAnchor: svgIconAnchor,
    className: 'leaflet-div-icon-transparent'
});

const iconaDirezioneTracking = L.divIcon({
    html: svgDirectionIconHtml,
    iconSize: [60, 60],
    iconAnchor: [30, 30],
    className: 'leaflet-div-icon-transparent'
});

const iconaBarca = L.icon({
    iconUrl: './icons/BOAT.png',
    iconSize: new L.point(46, 46),
    iconAnchor: [19, 19],
});

const heliIcon = L.icon({
    iconUrl: './icons/PosizioneElicottero.png',
    iconSize: new L.point(40, 40),
    iconAnchor: [20, 20],
});

const generalRel = L.icon({
    iconUrl: './icons/PuntoGPS.png',
    iconSize: new L.point(20, 20),
    iconAnchor: [10, 10],
});

const imageIcon = L.icon({
    iconUrl: './icons/IconaFoto.png',
    iconSize: new L.point(40, 40),
    iconAnchor: [16, 16],
});

const dropIcon = L.icon({
    iconUrl: './icons/IconaDrop.png',
    iconSize: new L.point(40, 40),
    iconAnchor: [16, 16],
});

export default function FlyObject(props) {
    const { bindTap } = useTapBindings();
    const isMob = props.isMob;
    const [rilevazioniState, setRilevazioni] = useState(props.rilevazioni);
    const [isOpenImage, setIsOpenImage] = useState(false);
    const [isOpenImageResponsive, setIsOpenImageResponsive] = useState(false);
    const [classOpenImage, setOpenImage] = useState("");
    const [classOpenImageResponsive, setOpenImageResponsive] = useState("");
    const [borderImages, setBorderImages] = useState("");
    const [borderImagesResponsive, setBorderImagesResponsive] = useState("initialStateContenitoreResponsive");
    const [colorAutoAssign, setColorAutoAssign] = useState(generateRandomColor());
    const [marker, setMarker] = useState(null);
    const [useButtonStatIcon, setUseButtonStatIcon] = useState(false);
    const [lastResetPktNum, setLastResetPktNum] = useState(null);
    const [lastButtonStat, setLastButtonStat] = useState(null);
    const [currentIconType, setCurrentIconType] = useState('tracking');

    // console.log("[FLYOBJECT]");

    useEffect(() => {
        setRilevazioni(props.rilevazioni);
        
        if (props.rilevazioni && props.rilevazioni.length > 0) {
            const lastPoint = props.rilevazioni[props.rilevazioni.length - 1];
            
            if (props.SN === "AT-00001" || props.SN === "AT-00000") {
                if (lastPoint.point.info.BUTTON_STAT && 
                    (!lastResetPktNum || lastPoint.point.info.PKT_NUM > lastResetPktNum)) {
                    setLastButtonStat(lastPoint.point.info.BUTTON_STAT);
                    setUseButtonStatIcon(true);
                }
                else if (lastButtonStat && (!lastResetPktNum || lastPoint.point.info.PKT_NUM > lastResetPktNum)) {
                    setUseButtonStatIcon(true);
                }
            }
        }
    }, [props.rilevazioni, lastResetPktNum]);

    useEffect(() => {
        isOpenImage ? setOpenImage("dimensioniImmagini") : setOpenImage("");
        isOpenImage ? setBorderImages("contenitoreBorderImages") : setBorderImages("");

        const popupContent = document.querySelector('.selez1');
        if (popupContent) {
            if(isOpenImage){
                popupContent.style.width = '760px';
            }
            else{
                popupContent.style.width = '300px';
            }
        }

        isOpenImageResponsive ? setOpenImageResponsive("openImagePopup-cell") : setOpenImageResponsive("");
        isOpenImageResponsive ? setBorderImagesResponsive("contenitoreBorderImagesResponsive") : setBorderImagesResponsive("initialStateContenitoreResponsive");
        
        const popupContentCell = document.querySelector('.selez1Responsive');
        if (popupContentCell) {
            if(isOpenImageResponsive){
                popupContentCell.style.width = '350px';
            }
            else{
                popupContentCell.style.width = '180px';
            }
        }

    },[isOpenImage, isOpenImageResponsive]);

    useEffect(() => {
        let iconTypeToNotify = 'tracking';
        if (useButtonStatIcon && lastButtonStat) {
            switch(lastButtonStat) {
                case 1:
                    iconTypeToNotify = 'normal';
                    break;
                case 2:
                    iconTypeToNotify = 'abnormal';
                    break;
                case 3:
                    iconTypeToNotify = 'emergency';
                    break;
                default:
                    iconTypeToNotify = 'tracking';
            }
        } else {
            iconTypeToNotify = 'tracking';
        }
        setCurrentIconType(iconTypeToNotify);
        props.onIconTypeChange(props.SN, iconTypeToNotify);
    }, [useButtonStatIcon, lastButtonStat]);

    const handleMarkerMounted = (ref) => {
        if (ref) {
          setMarker(ref);
        }
      };

    const handleMarkerAdd = () => {
        if (marker) {
            marker.setRotationOrigin('center center');
        }
    };

    function generateRandomColor(){
        let maxVal = 0xFFFFFF; // 16777215
        let randomNumber = Math.random() * maxVal;
        randomNumber = Math.floor(randomNumber);
        randomNumber = randomNumber.toString(16);
        let randColor = randomNumber.padStart(6, 0);
        return `#${randColor.toUpperCase()}`
    }

    const optionsPolyline = {
        weight:2,
        color:colorAutoAssign,
    };

    const toggleOpenCloseImagePopup = () => {
        setIsOpenImage(!isOpenImage);

        isOpenImage ? setOpenImage("dimensioniImmagini") : setOpenImage("");

    };

    const openPanel = () => {
        //console.log("open panel FlyObj.js");
        const navbar = document.querySelector('.barraNavigazione');
        if(navbar){
            navbar.style.transition = 'margin-top 0.5s ease'; // Aggiungi l'effetto di transizione
            navbar.style.marginTop = '0px';
        }

        const bottone = document.querySelector('.contenitoreIcona2');
        if(bottone){
            bottone.classList.remove('visible');
            bottone.classList.add('invisible');
        }

        const controlPanel = document.querySelector('.ControlPanelLaterale');
        if(controlPanel){
            controlPanel.style.transition = 'margin-top 0.5s ease'; // Aggiungi l'effetto di transizione
            controlPanel.style.marginTop = '0px';
        }
    }
    
    const closePanel = () => {
        //console.log("close panel FlyObj.js");

        const navbar = document.querySelector('.barraNavigazione');
        if(navbar){
            navbar.style.transition = 'margin-top 0.5s ease'; // Aggiungi l'effetto di transizione
            const altezzaNavbar = navbar.offsetHeight;
            navbar.style.marginTop = `-${altezzaNavbar}px`;    
        }

        setTimeout(() => {
            const bottone = document.querySelector('.contenitoreIcona2');
            if(bottone){
                bottone.classList.remove('invisible');
                bottone.classList.add('visible');
            }
        }, 10);
    }

    const handleImageClick = () => {
        setIsOpenImage(!isOpenImage);
        isOpenImage ? setOpenImage("dimensioniImmagini") : setOpenImage("");
        isOpenImage ? setBorderImages("contenitoreBorderImages") : setBorderImages("");

        if(isOpenImage){
            openPanel();
        }
        else
        {
            closePanel();
        }
        
        // Modifica le dimensioni del popup utilizzando il metodo CSS
        const popupContent = document.querySelector('.selez1');
        if (popupContent) {
            if(isOpenImage){
                popupContent.style.width = '760px';
            }
            else{
                popupContent.style.width = '300px';
            }
        }

          if(props.infoboxStates[props.ALIAS] == false){
            
          }
          else{
            props.toggleFollow(props.ALIAS);
          }

    };

    const toggleOpenCloseImagePopupResponsive = () => {
        setIsOpenImageResponsive(!isOpenImageResponsive);
        isOpenImageResponsive ? setOpenImageResponsive("openImagePopup-cell") : setOpenImageResponsive("");
        isOpenImageResponsive ? setBorderImagesResponsive("contenitoreBorderImagesResponsive") : setBorderImagesResponsive("initialStateContenitoreResponsive");
        // Modifica le dimensioni del popup utilizzando il metodo CSS
        const popupContent = document.querySelector('.selez1Responsive');
        if (popupContent) {
            if(isOpenImage){
                popupContent.style.width = '350px';
            }
            else{
                popupContent.style.width = '180px';
            }
        }

          if(props.infoboxStates[props.ALIAS] == false){
            
          }
          else{
            props.toggleFollow(props.ALIAS);
          }
    };

    const handleResetIcon = () => {
        if (rilevazioniState && rilevazioniState.length > 0) {
            const lastPoint = rilevazioniState[rilevazioniState.length - 1];
            setLastResetPktNum(lastPoint.point.info.PKT_NUM);
            setLastButtonStat(null);
            setUseButtonStatIcon(false);
        }
    };

    let pCoord = rilevazioniState.map((singlePoint) => {
        return [singlePoint.point.points.LATITUDE, singlePoint.point.points.LONGITUDE];
    })

    const handleOpenPopup = (e) => {
      
        const popupContainer = e.popup.getElement();
        popupContainer.style.width = '300px';

        if(isOpenImage == true){
            setIsOpenImage(false);
        }

    };

    const handleClosePopup = (e) => {
      
        openPanel();

    };

    const handleOpenPopupResponsive = (e) => {

        
        setIsOpenImageResponsive(false);

        const popupContainer = e.popup.getElement();
        popupContainer.style.width = '180px';

        if(isOpenImageResponsive == true){
            setIsOpenImageResponsive(false);
        }

    };

        return (<div>
                {
                    rilevazioniState.map((singlePoint, index) => {
                        // Se è un dispositivo AT-00000 o AT-00001, mostra solo l'ultimo punto
                        if ((props.SN === "AT-00001" || props.SN === "AT-00000") && index !== rilevazioniState.length - 1) {
                            return null;
                        }

                        let numberRilevazioni = rilevazioniState.length;
                        
                        let position = [singlePoint.point.points.LATITUDE, singlePoint.point.points.LONGITUDE];

                        const latDms = decimalToDms(singlePoint.point.points.LATITUDE);
                        const lonDms = decimalToDms(singlePoint.point.points.LONGITUDE);
                        let gradiLat = latDms.degrees;
                        let minutiLat = latDms.minutes;
                        let secondiLat = latDms.seconds;
                        let gradiLon = lonDms.degrees;
                        let minutiLon = lonDms.minutes;
                        let secondiLon = lonDms.seconds;
                        
                        let iconToRender = generalRel;
                        let currentIconTypeForRender = 'gps';
                        let imgExist = false;
                        let iconDirectionAngle = singlePoint.Direction;
                        let showDirectionMarker = false;
                        let directionIconToUse = iconaDirezioneTracking;

                        if ( singlePoint.point.images !== undefined) {

                            // if ( singlePoint.point.images.RGB_IMAGE !== "" || singlePoint.point.images.IR_IMAGE !== "") {
                            //     iconToRender = imageIcon;
                            //     imgExist = true;
                            //     iconDirectionAngle = 0;
                            // }

                            if ( singlePoint.point.images.RGB_IMAGE === "" && singlePoint.point.images.IR_IMAGE === "") {
                                iconToRender = imageIcon;
                                imgExist = true;
                                iconDirectionAngle = 0;
                            }

                        }

                        // "drop" minuscolo: marker goccia dei device nuovi.
                        // "DROP" maiuscolo resta sulla logica immagini e non va intercettato qui.
                        if (singlePoint.point.drop != null && typeof singlePoint.point.drop === 'object') {
                            iconToRender = dropIcon;
                            iconDirectionAngle = 0;
                        }

                        if (index == rilevazioniState.length-1) {
                            let mainIconToRender = generalRel;
                            let mainIconDirectionAngle = singlePoint.Direction;

                            
                            if (props.SN === "AT-00001" || props.SN === "AT-00000") {
                                mainIconDirectionAngle = 0;
                                if (useButtonStatIcon && lastButtonStat) {
                                    switch(lastButtonStat) {
                                        case 1:
                                            mainIconToRender = iconaNormal;
                                            currentIconTypeForRender = 'normal';
                                            showDirectionMarker = true;
                                            directionIconToUse = iconaDirezioneTracking;
                                            break;
                                        case 2:
                                            mainIconToRender = iconaAbnormal;
                                            currentIconTypeForRender = 'abnormal';
                                            showDirectionMarker = true;
                                            directionIconToUse = iconaDirezioneTracking;
                                            break;
                                        case 3:
                                            mainIconToRender = iconaEmergency;
                                            currentIconTypeForRender = 'emergency';
                                            showDirectionMarker = true;
                                            directionIconToUse = iconaDirezioneTracking;
                                            break;
                                        default:
                                            mainIconToRender = iconaTracking;
                                            currentIconTypeForRender = 'tracking';
                                            showDirectionMarker = true;
                                            directionIconToUse = iconaDirezioneTracking;
                                    }
                                } else {
                                    mainIconToRender = iconaTracking;
                                    currentIconTypeForRender = 'tracking';
                                    showDirectionMarker = true;
                                    directionIconToUse = iconaDirezioneTracking;
                                }
                            }
                            else{
                                mainIconToRender = heliIcon;
                            }
                            
                            iconToRender = mainIconToRender;
                            iconDirectionAngle = mainIconDirectionAngle;
                            currentIconTypeForRender = currentIconType;
                        }

                        //console.log("iconDirectionAngle", iconDirectionAngle);

                        if ( index == numberRilevazioni-1) {
                            //map.flyTo(position);
                        }

                        const isLastPoint = index === rilevazioniState.length - 1;
                        const pointId = `${singlePoint.point.info.MISSION_NUM}-${singlePoint.point.info.PKT_NUM}`;
                        const fragmentKey = isLastPoint ? `fragment-${props.SN}-live` : `fragment-${pointId}`;
                        const markerKey = isLastPoint ? `marker-${props.SN}-live` : `marker-${pointId}`;
                        const directionKey = isLastPoint ? `marker-direction-${props.SN}-live` : `marker-direction-${pointId}`;
                        const MarkerComponent = isLastPoint ? LiveMarker : Marker;
                        // Leaflet ordina per latitudine: senza offset alto il live finisce sotto i punti traccia.
                        const liveZIndexOffset = 1000;
                        const directionZIndexOffset = 999;

                        // Renderizzazione separata per mobile e desktop
                        if (!isMob) {
                            //console.log(`[FlyObject Desktop] Re-rendering icon with circle`);
                            return (
                                <React.Fragment key={fragmentKey}>
                                {/* SI POTEVA USARE ANCHE UN DIV INVECE DI REACT.FRAGMENT , PERO' AVREMMO CREATO UN ELEMENTO DOM IN PIU' PER I MARKER TRACKING E DIREZIONE , MENTRE COSI , I DUE MARKER VENGONO AGGIUNTI AL DOM COME UNICO ELEMENTO DOM */}

                                    {/* Marker per la direzione (solo se necessario e per l'ultimo punto) */}
                                    {isLastPoint && showDirectionMarker && (
                                        <LiveMarker
                                            key={directionKey}
                                            position={position}
                                            icon={directionIconToUse}
                                            rotationAngle={singlePoint.Direction}
                                            // rotationAngle={singlePoint.point.points.HEADING}
                                            zIndexOffset={directionZIndexOffset}
                                        />
                                    )}
                                    {/* Marker Principale */}
                                    <MarkerComponent
                                        key={markerKey}
                                        position={position}
                                        icon={iconToRender}
                                        rotationAngle={iconDirectionAngle}
                                        zIndexOffset={isLastPoint ? liveZIndexOffset : undefined}
                                        eventHandlers={{
                                            add: handleMarkerAdd,
                                            popupopen : handleOpenPopup,
                                            popupclose: handleClosePopup
                                        }}
                                        {...(isLastPoint
                                            ? { onMarkerRef: handleMarkerMounted }
                                            : { ref: handleMarkerMounted })}
                                    >
                                        <Popup className="request-popup selez1"  /* onClose={handleClosePopup} style={{ width: popupWidth }}*/>
                                        <div className={"request-popup selez2 "+ borderImages}>
                                            <div className={"coordinate d-flex flex-column p-1 selez3 "+ borderImages}>
                                                <div className="mt-1 w-100 d-flex flex-column flex-center">
                                                    
                                                        <div className="flex-center">
                                                            <h5 className="mr-2">Lat: </h5>
                                                            <h5 className="mr-1">{gradiLat.toFixed(0)}°</h5>
                                                            <h5 className="mr-1">{minutiLat.toFixed(0)}'</h5>
                                                            <h5 className="mr-1">{secondiLat.toFixed(3)}''</h5>
                                                        </div>
                                                    
                                                        <div className="flex-center">
                                                            <h5 className="mr-2">Lon:</h5>
                                                            <h5 className="mr-1">{gradiLon.toFixed(0)}°</h5>
                                                            <h5 className="mr-1">{minutiLon.toFixed(0)}'</h5>
                                                            <h5 className="mr-1">{secondiLon.toFixed(3)}''</h5>
                                                        </div>

                                                        {index === rilevazioniState.length - 1 && (props.userTipo === 2 || props.userTipo === 3) && (
                                                            <div className="flex-center">
                                                                <h5 className="mr-2">PKT_NUM:</h5>
                                                                <h5>{singlePoint.point.info.PKT_NUM}</h5>
                                                            </div>
                                                        )}
                                                    
                                                </div>
                                                <div className="mt-2">
                                                    <span className="h6">Rilevato alle:&nbsp;
                                                        <Moment format= "HH:mm:ss" date={singlePoint?.point?.time?.YEAR + "-" + singlePoint?.point?.time?.MONTH + "-" + singlePoint?.point?.time?.DAY + " " + singlePoint?.point?.time?.HOUR + ":" + singlePoint?.point?.time?.MINUTE + ":" + singlePoint?.point?.time?.SECOND} ></Moment>
                                                        {/*&nbsp;( <Moment
                                                                parse="YYYY-MM-DD HH:mm:ss"
                                                                date={  singlePoint?.point?.time?.YEAR + "-" +
                                                                        singlePoint?.point?.time?.MONTH + "-" +
                                                                        singlePoint?.point?.time?.DAY + " " +
                                                                        singlePoint?.point?.time?.HOUR + ":" +
                                                                        singlePoint?.point?.time?.MINUTE + ":" +
                                                                        singlePoint?.point?.time?.SECOND
                                                                    }
                                                                toNow ago trim
                                                        >
                                                        </Moment> fa. )*/}
                                                    </span>
                                                </div>
                                                {useButtonStatIcon && (props.SN === "AT-00001" || props.SN === "AT-00000") && (
                                                    <div className="mt-2">
                                                        <button 
                                                            className="btn btn-sm btn-primary"
                                                            onClick={handleResetIcon}
                                                        >
                                                            Ripristina Tracking
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                            <div className={"d-flex flex-column p-2 selez3 "+ borderImages}>
                                                {/* imgExist ? (<div className="mb-1">Burns point: {singlePoint?.point?.images.NUM_BURN_POINTS}</div>) : "" */}
                                                <div className="d-flex flex-row coordinate ">
                                                    {/* imgExist ? <img onClick={toggleOpenCloseImagePopup} className = {"img-responsive mini-img-popup m-2 " + classOpenImage} src={`data:image/jpeg;base64,${singlePoint.point.images.RGB_IMAGE}`} /> : "" */}
                                                    {/* imgExist ? <img onClick={toggleOpenCloseImagePopup} className = {"img-responsive mini-img-popup m-2 " + classOpenImage} src={`data:image/jpeg;base64,${singlePoint.point.images.IR_IMAGE}`} /> : "" */}
                                                    
                                                    {/*    QUELLO CHE STA ORA IN PRODUZIONE   */}
                                                    {/*    MOMENTANEMENTE NON VUOLE CHE SI APRA LA FOTO */}
                                                    {/* { imgExist ? <img onClick={handleImageClick} className = {"img-responsive mini-img-popup-pc m-2 " + classOpenImage} src={`data:image/jpeg;base64,${singlePoint.point.images.RGB_IMAGE}`} /> : ""  }
                                                    { imgExist ? <img onClick={handleImageClick} className = {"img-responsive mini-img-popup-pc m-2 " + classOpenImage} src={`data:image/jpeg;base64,${singlePoint.point.images.IR_IMAGE}`} /> : ""  } */}

                                                    {/*    QUELLO CHE DI TEST   */}
                                                    {/* <img onClick={handleImageClick} className = {"img-responsive mini-img-popup-pc m-2 " + classOpenImage} src={'./brandIcons/USERS.png'} /> */}
                                                    {/* <img onClick={handleImageClick} className = {"img-responsive mini-img-popup-pc m-2 " + classOpenImage} src={'./brandIcons/USERS.png'} /> */}

                                                </div>

                        {/*
                                                <div className="d-flex flex-row popup-telemetria">
                                                    <div className="d-flex flex-row p-2">
                                                        <div className="mr-1"><i className={"fad fa-code-commit " + (!singlePoint.point.telemetria.GPIO_STATUS ? " disable-icon-telemetria" : "" )}></i></div>
                                                        <div className="mr-1"><i className={"fad fa-location-circle "  + (!singlePoint.point.telemetria.GPIO_STATUS ? " disable-icon-telemetria" : "" )}></i></div>
                                                        <div className="mr-1"><i className={"fad fa-video " + (!singlePoint.point.telemetria.IR_STAT ? " disable-icon-telemetria" : "" )}></i></div>
                                                        <div className="mr-1"><i className={"fad fa-image-polaroid "  + (!singlePoint.point.telemetria.RGB_STAT ? " disable-icon-telemetria" : "" )}></i></div>
                                                    </div>
                                                    <div className="d-flex flex-row ml-auto">
                                                        <div className="p-2">
                                                            <i className={"fal fa-satellite " }></i>&nbsp;
                                                            <span className="small">{singlePoint.point.points.SAT_VIS}</span>/<span className="small">{singlePoint.point.points.SAT_USED}</span>
                                                        </div>

                                                    </div>
                                                    <div className="mb-1 small ml-auto p-2">idPoint: {singlePoint.point.info.MISSION_NUM}-{singlePoint.point.info.PKT_NUM}</div>
                                                </div>
                        */}

                                            </div>
                                        </div>
                                    </Popup>
                                </MarkerComponent>
                            </React.Fragment>
                            );
                            }else {
                                // Log per il re-render dell'icona con cerchio (Mobile)
                                if (isLastPoint && ['normal', 'abnormal', 'emergency'].includes(currentIconTypeForRender)) {
                                    //console.log(`[FlyObject Mobile] Re-rendering icon with circle for SN: ${props.SN}, State: ${currentIconTypeForRender}`);
                                }
                                const fragmentKeyResp = isLastPoint ? `fragment-resp-${props.SN}-live` : `fragment-resp-${pointId}`;
                                const markerKeyResp = isLastPoint ? `marker-resp-${props.SN}-live` : `marker-resp-${pointId}`;
                                const directionKeyResp = isLastPoint ? `marker-direction-resp-${props.SN}-live` : `marker-direction-resp-${pointId}`;

                                return (
                                    <React.Fragment key={fragmentKeyResp}>
                                        {/* Marker per la direzione (solo se necessario e per l'ultimo punto) */}
                                        {isLastPoint && showDirectionMarker && (
                                            <LiveMarker
                                                key={directionKeyResp}
                                                position={position}
                                                icon={directionIconToUse}
                                                rotationAngle={singlePoint.Direction}
                                                zIndexOffset={directionZIndexOffset}
                                            />
                                        )}
                                        {/* Marker Principale */}
                                        <MarkerComponent
                                            key={markerKeyResp}
                                            position={position}
                                            icon={iconToRender}
                                            rotationAngle={iconDirectionAngle}
                                            zIndexOffset={isLastPoint ? liveZIndexOffset : undefined}
                                            eventHandlers={{
                                                add: handleMarkerAdd,
                                                popupopen : handleOpenPopupResponsive
                                            }}
                                            {...(isLastPoint
                                                ? { onMarkerRef: handleMarkerMounted }
                                                : { ref: handleMarkerMounted })}
                                        >

                                        <Popup className="request-popup selez1Responsive">
                                            <div className={"divPopup "+ borderImagesResponsive}>
                                                <div className={"coordinate d-flex flex-column "+ borderImagesResponsive}>
                                                <div className="mt-1 w-100 d-flex flex-column flex-center">
                                                        
                                                        <div className="flex-center">
                                                            <h7 className="mr-2 font-weight-responsive">Lat: </h7>
                                                            <h7 className="mr-1">{gradiLat.toFixed(0)}°</h7>
                                                            <h7 className="mr-1">{minutiLat.toFixed(0)}'</h7>
                                                            <h7 className="mr-1">{secondiLat.toFixed(3)}''</h7>
                                                        </div>
                                                    
                                                        <div className="flex-center">
                                                            <h7 className="mr-2 font-weight-responsive">Lon:</h7>
                                                            <h7 className="mr-1">{gradiLon.toFixed(0)}°</h7>
                                                            <h7 className="mr-1">{minutiLon.toFixed(0)}'</h7>
                                                            <h7 className="mr-1">{secondiLon.toFixed(3)}''</h7>
                                                        </div>

                                                        {index === rilevazioniState.length - 1 && (props.userTipo === 2 || props.userTipo === 3) && (
                                                            <div className="flex-center">
                                                                <h7 className="mr-2 font-weight-responsive">PKT_NUM:</h7>
                                                                <h7>{singlePoint.point.info.PKT_NUM}</h7>
                                                            </div>
                                                        )}
                                                    
                                                </div>
                                                    <div className="mt-2"><span className="h7 font-weight-responsive">Rilevato alle:&nbsp;
                                                        <Moment format= "HH:mm:ss" date={singlePoint?.point?.time?.YEAR + "-" + singlePoint?.point?.time?.MONTH + "-" + singlePoint?.point?.time?.DAY + " " + singlePoint?.point?.time?.HOUR + ":" + singlePoint?.point?.time?.MINUTE + ":" + singlePoint?.point?.time?.SECOND} ></Moment>
                                                        {/*&nbsp;( <Moment
                                                                parse="YYYY-MM-DD HH:mm:ss"
                                                                date={  singlePoint?.point?.time?.YEAR + "-" +
                                                                        singlePoint?.point?.time?.MONTH + "-" +
                                                                        singlePoint?.point?.time?.DAY + " " +
                                                                        singlePoint?.point?.time?.HOUR + ":" +
                                                                        singlePoint?.point?.time?.MINUTE + ":" +
                                                                        singlePoint?.point?.time?.SECOND
                                                                    }
                                                                toNow ago trim
                                                        >
                                                                </Moment> fa. )*/}
                                                        </span>
                                                    </div>
                                                </div>
                                                
                                                {/* GESTIONE TRACKERS */}
                                                {useButtonStatIcon && (props.SN === "AT-00001" || props.SN === "AT-00000") && (
                                                    <div className="mt-2">
                                                        <button 
                                                            className="btn btn-sm btn-primary"
                                                            {...bindTap(handleResetIcon)}
                                                        >
                                                            Ripristina Tracking
                                                        </button>
                                                    </div>
                                                )}

                                                <div className={"d-flex flex-column pt-1 "+ borderImagesResponsive} >
                                                    {/* imgExist ? (<div className="mb-1">Burns point: {singlePoint?.point?.images.NUM_BURN_POINTS}</div>) : "" */} 
                                                    <div className="d-flex flex-row coordinate">
                                                        { /* imgExist ? <img onClick={toggleOpenCloseImagePopupResponsive} className = {"img-responsive mini-img-popup m-2 " + classOpenImage} src={`data:image/jpeg;base64,${singlePoint.point.images.RGB_IMAGE}`} /> : "" */}
                                                        { /* imgExist ? <img onClick={toggleOpenCloseImagePopupResponsive} className = {"img-responsive mini-img-popup m-2 " + classOpenImage} src={`data:image/jpeg;base64,${singlePoint.point.images.IR_IMAGE}`} /> : "" */}
                                                        
                                                        {/*    MOMENTANEMENTE NON VUOLE CHE SI APRA LA FOTO */}
                                                        {/* { imgExist ? <img onClick={toggleOpenCloseImagePopupResponsive} className = {"img-responsive mini-img-popup-cell m-2 " + classOpenImageResponsive} src={`data:image/jpeg;base64,${singlePoint.point.images.RGB_IMAGE}`} /> : ""  }
                                                        { imgExist ? <img onClick={toggleOpenCloseImagePopupResponsive} className = {"img-responsive mini-img-popup-cell m-2 " + classOpenImageResponsive} src={`data:image/jpeg;base64,${singlePoint.point.images.IR_IMAGE}`} /> : ""  } */}
                                                    </div>

                            {/*
                                                    <div className="d-flex flex-row popup-telemetria">
                                                        <div className="d-flex flex-row p-1">
                                                            <div className="mr-1"><i className={"fad fa-code-commit " + (!singlePoint.point.telemetria.GPIO_STATUS ? " disable-icon-telemetria" : "" )}></i></div>
                                                            <div className="mr-1"><i className={"fad fa-location-circle "  + (!singlePoint.point.telemetria.GPIO_STATUS ? " disable-icon-telemetria" : "" )}></i></div>
                                                            <div className="mr-1"><i className={"fad fa-video " + (!singlePoint.point.telemetria.IR_STAT ? " disable-icon-telemetria" : "" )}></i></div>
                                                            <div className="mr-1"><i className={"fad fa-image-polaroid "  + (!singlePoint.point.telemetria.RGB_STAT ? " disable-icon-telemetria" : "" )}></i></div>
                                                        </div>
                                                        <div className="d-flex flex-row ml-auto">
                                                            <div className="p-1">
                                                                <i className={"fal fa-satellite " }></i>&nbsp;
                                                                <span className="small">{singlePoint.point.points.SAT_VIS}</span>/<span className="small">{singlePoint.point.points.SAT_USED}</span>
                                                            </div>

                                                        </div>
                                                        <div className="small ml-auto p-1">idPoint: {singlePoint.point.info.MISSION_NUM}-{singlePoint.point.info.PKT_NUM}</div>
                                                    </div>
                            */}

                                                </div>
                                            </div>
                                        </Popup>
                                    </MarkerComponent>
                                </React.Fragment>  
                                  );
                              }
                        
                    }
                    )
                }

                {/* Mostra la polyline solo se non è un dispositivo AT-00000 o AT-00001 */}
                {!(props.SN === "AT-00001" || props.SN === "AT-00000") && (
                    <Polyline positions={pCoord} pathOptions={optionsPolyline} />
                )}
            </div>
        );
}
